import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { api } from '../../services/api';
import { performOCR, extractFundNames, terminateWorker } from '../../services/ocrService';
import './ScanUploader.css';

const ScanUploader = ({ onUploadComplete, onError }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  // Cleanup OCR worker on unmount
  useEffect(() => {
    return () => {
      terminateWorker();
    };
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setSelectedFile(file);

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviewUrl(e.target.result);
        };
        reader.readAsDataURL(file);
      } else if (file.type === 'application/pdf') {
        setPreviewUrl('/pdf-icon.svg');
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.bmp', '.tiff'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  // Client-side OCR processing for images
  const processWithClientOCR = async () => {
    setStatusMessage('While you wait, close your eyes and visualize your goals');
    setUploadProgress(10);

    // Perform OCR using Tesseract.js
    const ocrResult = await performOCR(selectedFile, (progress) => {
      // Map OCR progress (0-100) to our progress (10-70)
      const mappedProgress = 10 + Math.round(progress * 0.6);
      setUploadProgress(mappedProgress);
      // Update message based on progress
      if (progress > 80) {
        setStatusMessage('Almost there... Extracting text');
      } else if (progress > 50) {
        setStatusMessage('Reading your investments...');
      }
    });

    setUploadProgress(75);
    setStatusMessage('Matching funds with database...');

    // Extract potential fund names from OCR text
    const fundNames = extractFundNames(ocrResult.text);

    // Send text to backend for fund matching
    const matchResponse = await api.matchFundsFromText(
      ocrResult.text,
      fundNames,
      ocrResult.confidence
    );

    setUploadProgress(100);
    const matchedCount = matchResponse.data.matched_funds?.length || 0;
    setStatusMessage(matchedCount > 0
      ? `Found ${matchedCount} fund${matchedCount !== 1 ? 's' : ''}!`
      : 'Processing complete');

    return {
      scanId: 'client-ocr-' + Date.now(),
      status: 'completed',
      platform: matchResponse.data.platform,
      extracted_funds: matchResponse.data.potential_fund_names?.map(name => ({ name })) || [],
      matched_funds: matchResponse.data.matched_funds || [],
      ocr_confidence: ocrResult.confidence,
      ocr_method: 'client-side (Tesseract.js)'
    };
  };

  // Server-side OCR processing (fallback for PDFs)
  const processWithServerOCR = async () => {
    setStatusMessage('Uploading to server...');

    // Upload file
    const uploadResponse = await api.uploadScan(selectedFile);
    const uploadResult = uploadResponse.data;
    setUploadProgress(30);

    setStatusMessage('Processing with server OCR...');

    // Process the scan
    await api.processScan(uploadResult.scan_id);
    setUploadProgress(70);

    // Get results
    const resultsResponse = await api.getScanResults(uploadResult.scan_id);
    setUploadProgress(100);

    return {
      scanId: uploadResult.scan_id,
      ...resultsResponse.data,
      ocr_method: 'server-side'
    };
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(0);
    setStatusMessage('Starting...');

    try {
      let results;

      // Use client-side OCR for images (no server fallback - server doesn't have OCR in production)
      if (selectedFile.type.startsWith('image/')) {
        results = await processWithClientOCR();
      } else {
        // PDF - show not supported message (server OCR not available in production)
        throw new Error('PDF scanning is not supported yet. Please upload a screenshot image (JPG, PNG) instead.');
      }

      // Call parent callback with results
      if (onUploadComplete) {
        onUploadComplete(results);
      }

      // Reset state after short delay
      setTimeout(() => {
        setSelectedFile(null);
        setPreviewUrl(null);
        setUploadProgress(0);
        setUploading(false);
        setStatusMessage('');
      }, 1000);

    } catch (error) {
      console.error('Error during scan:', error);
      let errorMessage = error.message || 'Unknown error';

      // Handle timeout errors
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Please try again with a smaller image.';
      }
      // Handle API errors
      else if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'object' && detail.error) {
          errorMessage = detail.error + '\n\n' + detail.message + '\n\n' + detail.solution;
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      // Handle network errors
      else if (error.message === 'Network Error') {
        errorMessage = 'Network error. Please check your internet connection.';
      }

      if (onError) {
        onError(new Error(errorMessage));
      }
      setUploading(false);
      setUploadProgress(0);
      setStatusMessage('');
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadProgress(0);
    setStatusMessage('');
  };

  return (
    <div className="scan-uploader">
      {/* Loading Overlay */}
      {uploading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="bull-container">
              <img
                src="/image_bull.png"
                alt="Wall Street Bull"
                className="bull-image-pulsating"
              />
            </div>
            <h3 className="loading-message">
              {statusMessage || 'Processing your screenshot...'}
            </h3>
            <p className="loading-submessage">
              {selectedFile?.type.startsWith('image/')
                ? 'OCR running in your browser - no data leaves your device'
                : 'Uploading to server for processing'}
            </p>
            <div className="progress-bar-wrapper">
              <div className="progress-bar-modern">
                <div
                  className="progress-fill-modern"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span className="progress-text">{uploadProgress}%</span>
            </div>
          </div>
        </div>
      )}

      {!selectedFile ? (
        <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
          <input {...getInputProps()} />
          <div className="dropzone-content">
            <svg className="upload-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <h3>Drop your investment screenshot or PDF here</h3>
            <p>or click to browse files</p>
            <div className="supported-formats">
              Supported: JPG, PNG, PDF (max 10MB)
            </div>
            <div className="supported-apps">
              Works with: Groww, Zerodha, Paytm Money, Kuvera, ET Money, and more
            </div>
            <div className="privacy-note">
              Images are processed in your browser - your data stays private
            </div>
          </div>
        </div>
      ) : (
        <div className="preview-container">
          {previewUrl && (
            <div className="file-preview">
              <img src={previewUrl} alt="Preview" />
            </div>
          )}
          <div className="file-info">
            <h4>{selectedFile.name}</h4>
            <p>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
            {selectedFile.type.startsWith('image/') && (
              <span className="processing-badge">Browser OCR</span>
            )}
          </div>
          {uploading && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span>{uploadProgress}%</span>
            </div>
          )}
          <div className="actions">
            <button
              onClick={handleClear}
              disabled={uploading}
              className="btn-secondary"
            >
              Clear
            </button>
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="btn-primary"
            >
              {uploading ? 'Processing...' : 'Extract Funds'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScanUploader;
